import { InfoCircleOutlined } from '@ant-design/icons';
import { HasAccess } from "@permify/react-role";
import { Button, Col, Collapse, Empty, Modal, Row, Spin, Tag, Tooltip, message } from 'antd';
import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { deleteReservation, getReservations } from '../../../services/reservation';
import useReservationServices from '../hooks/useReservationServices';
import { getCookie } from './../../../services/cookies';
import DateSummary from './DateSummary';

const { Panel } = Collapse;

const ConfirmedReservationByDate = ({
  confirmedReservations,
  selectedDepartment,
  queryParams
}) => {
  const [loadingReservation, setLoadingReservation] = useState({ byBlock: false, services: false });
  const [modalVisible, setModalVisible] = useState(false);
  const [reservationsByBlock, setReservationsByBlock] = useState();
  const [reservationToDelete, setReservationToDelete] = useState();
  const location = useLocation();
  const [activeAccordion, setActiveAccordion] = useState();
  const reservationServices = useReservationServices();
  const cookieBranchOfficeId = getCookie("branch_office_id");
  const { t } = useTranslation();


  const fetchReservationsByBlock = useCallback(async (blockId) => {
    if (cookieBranchOfficeId) {
      setLoadingReservation(loadingReservation => { return { ...loadingReservation, byBlock: true } });
      let reservationParams = {
        department_id: selectedDepartment,
        block_id: blockId,
        ...queryParams
      };
      delete reservationParams['page'];

      const response = await getReservations(reservationParams, cookieBranchOfficeId);
      setReservationsByBlock(response.data);
      setLoadingReservation(loadingReservation => { return { ...loadingReservation, byBlock: false } });
    }
  }, []);

  const showModal = (reservation, blockId) => {
    const { id, number } = reservation;
    setModalVisible(true);
    setReservationToDelete({ id, number, blockId });
  };

  const closeModal = () => {
    setModalVisible(false);
    setReservationToDelete();
  };

  const reservationDelete = async (reservation) => {
    try {
      setLoadingReservation(loadingReservation => { return { ...loadingReservation, byBlock: true } });
      const response = await deleteReservation({ reservation_id: reservation.id });

      if (response === 1) {
        message.success({
          content: `${t('reservations.byDate.confirmedByDate.blockContent.reservation')} eliminada exitosamente`,
          style: {
            marginTop: '30vh',
            fontSize: 16
          }
        });
        closeModal();
        fetchReservationsByBlock(reservation.blockId);
        setLoadingReservation(loadingReservation => { return { ...loadingReservation, byBlock: false } });
      } else {
        message.error({
          content: 'Ocurrió un error. Inténtalo de nuevo.',
          style: {
            marginTop: '30vh',
            fontSize: 16
          }
        });
        closeModal();
        setLoadingReservation(loadingReservation => { return { ...loadingReservation, byBlock: false } });
      }

    } catch (err) {
      console.error('Error interno:', err);
      message.error({
        content: `Error interno: ${err}`,
        style: {
          marginTop: '30vh',
          fontSize: 16
        }
      });
      closeModal();
      setLoadingReservation(loadingReservation => { return { ...loadingReservation, byBlock: false } });
    }
  };

  const handlerServicesByReservation = (reservationId) => {
    reservationServices.setParams({ reservationId });
  }

  useEffect(() => {
    setActiveAccordion();
    setReservationsByBlock(null);
  }, [queryParams])


  useEffect(() => {
    if (activeAccordion) {
      fetchReservationsByBlock(activeAccordion);
    }
  }, [fetchReservationsByBlock, activeAccordion])

  const blockContent = (block) => {
    return (
      <Row justify="center" className="pt-20 pb-20">
        <Col lg={21} xs={22}>
          <Row className="block-content_header mb-10">
            <Col lg={3}>
              <p className="text-title ml-lg-15 mb-0">
                {t('reservations.byDate.confirmedByDate.blockContent.reservationNumber')}
              </p>
            </Col>
            <Col lg={6}>
              <p className="text-title text-center mb-0">
                {t('reservations.byDate.confirmedByDate.blockContent.companyTitle')}
              </p>
            </Col>
            <Col lg={4}>
              <p className="text-title text-center mb-0">
                {t('reservations.byDate.confirmedByDate.blockContent.servicesTitle')}
              </p>
            </Col>
            <Col xl={6} lg={5}>
              <p className="text-title text-center mb-0">
                {t('reservations.byDate.confirmedByDate.blockContent.totalPatients')}
                <Tooltip
                  title={t('reservations.byDate.confirmedByDate.blockContent.admittedPatients')}
                  className="ml-5"
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </p>
            </Col>
            <Col xl={5} lg={6} />
          </Row>
          {
            reservationsByBlock?.map((reservation) => {
              return (
                <Row key={reservation.id} className="block-content_body pt-10 pb-10 mb-10">
                  <Col lg={3} xs={12} className="block-content_body__item first">
                    <p className="mobile-title text-center mb-0">
                      {t('reservations.byDate.confirmedByDate.blockContent.reservationNumber')}
                    </p>
                    <p className="ml-lg-15 mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">{reservation.number}</p>
                  </Col>
                  <Col lg={6} xs={12} className="block-content_body__item">
                    <p className="mobile-title text-center mb-0">
                      {t('reservations.byDate.confirmedByDate.blockContent.companyTitle')}
                    </p>
                    <p className="text-center mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">{reservation.company.business_name}</p>
                  </Col>
                  <Col lg={4} xs={12} className="block-content_body__item">
                    <Tooltip
                      overlayClassName='see-services-tooltip'
                      overlayStyle={{ minWidth: 460 }}
                      trigger="click"
                      onClick={() => handlerServicesByReservation(reservation.id)}
                      color="#FFFFFF"
                      title={
                        <Spin spinning={reservationServices.isLoading}>
                          <Row justify="space-between">
                            <Col lg={12}>
                              <p className='title mb-10'>
                                {t('reservations.byDate.confirmedByDate.blockContent.serviceTitle')}
                              </p>
                            </Col>
                            <Col lg={6}>
                              <p className="title text-center mb-10">
                                {t('reservations.byDate.confirmedByDate.blockContent.patientsTitle')}
                              </p>
                            </Col>
                            <Col lg={6}>
                              <p className="title text-center mb-10">
                                {t('reservations.byDate.confirmedByDate.blockContent.blockTitle')}
                              </p>
                            </Col>
                          </Row>
                          {
                            reservationServices?.data?.filter(service => service.block === block.block)?.map((service) => {
                              return (
                                <Row justify="space-between" key={service.id}>
                                  <Col lg={12}>
                                    <p>{service.name}</p>
                                  </Col>
                                  <Col lg={6}>
                                    <p className="text-center">{service.total_patients}</p>
                                  </Col>
                                  <Col lg={6}>
                                    <p className="text-center">{service.block}hs</p>
                                  </Col>
                                </Row>
                              )
                            })
                          }
                        </Spin>
                      }
                    >
                      <p className="mobile-title text-center mb-0">
                        {t('reservations.byDate.confirmedByDate.blockContent.servicesTitle')}
                      </p>
                      <p className="text-center mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10" style={{ cursor: "pointer" }}>
                        Ver {t('reservations.byDate.confirmedByDate.blockContent.servicesTitle').toLocaleLowerCase()}
                      </p>
                    </Tooltip>
                  </Col>


                  <Col xl={6} lg={5} xs={12} className="block-content_body__item">
                    <p className="mobile-title text-center mb-0">
                      {t('reservations.byDate.confirmedByDate.blockContent.totalPatients')}
                    </p>
                    <p className="text-center mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">
                      {reservation.attended_patients}/{reservation.reserved_patients}
                    </p>
                  </Col>


                  <Col xl={5} lg={6} xs={24} className="block-content_body__item buttons">
                    <Row justify="space-between mr-lg-15" align="middle">
                      <Col className="mr-md-20 mr-sm-20 mr-xs-20">
                        <Link to={{ pathname: `/reservas/${reservation.id}/ver`, state: { from: location.pathname, search: location.search } }} >
                          <HasAccess
                            roles={['super_usuario', 'branch_office_manager', 'administracion']}
                            permissions='reservation-detail'
                          >
                            <Button className="cmt-button secondary outline" type="primary" size="small">
                              Ver más
                            </Button>
                          </HasAccess>
                        </Link>
                      </Col>
                      <Col className=" mr-md-20 mr-sm-20 mr-xs-20">
                        <Link to={{ pathname: `/admisiones/${reservation.id}`, state: { from: location.pathname, search: location.search } }} >
                          <HasAccess
                            roles={['super_usuario', 'branch_office_manager', 'administracion']}
                            permissions='admission'
                          >
                            <Button className="cmt-button secondary" type="primary" size="small">
                              Admisión
                            </Button>
                          </HasAccess>
                        </Link>
                      </Col>
                      <Col>
                        <HasAccess
                          roles={['super_usuario', 'branch_office_manager', 'administracion']}
                          permissions='delete-admission'
                        >
                          <Button
                            className={`delete-icon-button ${reservation.has_admissions && 'disabled'}`}
                            onClick={() => showModal(reservation, block.blockId)}
                            disabled={reservation.has_admissions}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 148.61 189.36"><g id="Capa_2" dataname="Capa 2"><g id="Capa_1-2" dataname="Capa 1"><path d="M47.16,21.25c0-3.94-.14-7.24,0-10.53C47.49,4.64,51.6.29,57.66.17q16.5-.33,33,0c6.37.11,10.48,4.42,10.78,10.79.15,3.28,0,6.58,0,10.4,11.53,0,22.48-.06,33.43,0,7.49,0,12.84,3.93,13.53,10a92.45,92.45,0,0,1,.07,10.16H.13c0-3.65-.34-7.14.08-10.54.69-5.58,5.89-9.6,12.18-9.65C23.71,21.19,35,21.25,47.16,21.25Zm46.89-.2c0-3.38,0-6.19,0-9C94,9,92.36,7.48,89.32,7.48c-10,0-20-.16-29.93.17-1.52,0-3.93,2.14-4.25,3.64-.67,3.1-.2,6.43-.2,9.76Z" /><path d="M15.42,54.5v4.63q0,58.15,0,116.31c0,10.25,4.24,13.91,16.09,13.91q42.55,0,85.09,0c12.36,0,16.25-3.36,16.25-14.11q0-57.93,0-115.88V54.5Z" /></g></g>
                            </svg>
                          </Button>
                        </HasAccess>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )
            })
          }
        </Col>
      </Row>
    );
  };


  return (
    <Row justify="center">
      <Col lg={22} xs={22}>
        <Spin spinning={confirmedReservations.isLoading}>
          {
            confirmedReservations?.data?.length > 0
              ? <Row>
                <Col lg={24} xs={24} className="confirmed-wrapper mt-lg-50 mt-md-20 mt-sm-20 mt-xs-20 mb-lg-30 mb-md-20 mb-sm-20 mb-xs-20">
                  <Row className="table-header">
                    <Col lg={2}>
                      <p className="mb-5 ml-15">
                        {t('reservations.byDate.confirmedByDate.blockTitle')}
                      </p>
                    </Col>
                    <Col lg={4}>
                      <p className="text-center mb-5">
                        {t('reservations.byDate.confirmedByDate.capacityTitle')}
                      </p>
                    </Col>
                    <Col lg={4}>
                      <p className="text-center mb-5">
                        {t('reservations.byDate.confirmedByDate.reservedTitle')}
                      </p>
                    </Col>
                    <Col lg={4}>
                      <p className="text-center mb-5">
                        {t('reservations.byDate.confirmedByDate.toConfirmTitle')}
                      </p>
                    </Col>
                    <Col lg={2}>
                      <p className="text-center mb-5">
                        {t('reservations.byDate.confirmedByDate.freeTitle')}
                      </p>
                    </Col>
                    <Col lg={3}>
                      <p className="text-center mb-5">
                        {t('reservations.byDate.confirmedByDate.servedTitle')}
                      </p>
                    </Col>
                  </Row>
                  <Row className="table-body mb-lg-10 mb-md-15 mb-sm-15 mb-xs-15">
                    <Collapse
                      accordion
                      bordered={false}
                      expandIconPosition="end"
                      style={{ width: '100%' }}
                      activeKey={activeAccordion}
                      onChange={(active) => {
                        setActiveAccordion(active);
                      }}
                    >
                      {
                        confirmedReservations?.data?.map((block) =>
                          <Panel
                            key={block.id_block}
                            showArrow={false}
                            header={
                              <Row
                                style={{ width: '100%' }}
                              >
                                <Col lg={2} xs={12} className="table-body__item-confirmed first-item">
                                  <p className="mobile-title mb-0">
                                    {t('reservations.byDate.confirmedByDate.blockTitle')}
                                  </p>
                                  <Tag className="ml-lg-15 mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">{block.block}</Tag>
                                </Col>
                                <Col lg={4} xs={12} className="table-body__item-confirmed">
                                  <p className="mobile-title text-center mb-0">
                                    {t('reservations.byDate.confirmedByDate.capacityTitle')}
                                  </p>
                                  <p className="text-center mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">{block.capacity}</p>
                                </Col>
                                <Col lg={4} xs={12} className="table-body__item-confirmed">
                                  <p className="mobile-title text-center mb-0">
                                    {t('reservations.byDate.confirmedByDate.reservedTitle')}
                                  </p>
                                  <p className="text-center mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">{block.reserved}</p>
                                </Col>
                                <Col lg={4} xs={12} className="table-body__item-confirmed">
                                  <p className="mobile-title text-center mb-0">
                                    {t('reservations.byDate.confirmedByDate.toConfirmTitle')}
                                  </p>
                                  <p className="text-center mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">{block.for_confirm}</p>
                                </Col>
                                <Col lg={2} xs={12} className="table-body__item-confirmed">
                                  <p className="mobile-title text-center mb-0">
                                    {t('reservations.byDate.confirmedByDate.freeTitle')}
                                  </p>
                                  <p className="text-center mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">{block.free}</p>
                                </Col>
                                <Col lg={3} xs={12} className="table-body__item-confirmed">
                                  <p className="mobile-title text-center mb-0">
                                    {t('reservations.byDate.confirmedByDate.servedTitle')}
                                  </p>
                                  <p className="text-center mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">{block.attended}</p>
                                </Col>
                                <Col lg={5} xs={24} className="table-body__item-confirmed arrow-item">
                                  <i className="icon-system-chevron-down mr-lg-15" />
                                </Col>
                              </Row>
                            }
                          >
                            {
                              loadingReservation.byBlock ?
                                <Row justify="center">
                                  <Spin className="custom-spinner" />
                                </Row>
                                :
                                blockContent(block)
                            }
                          </Panel>
                        )
                      }
                    </Collapse>
                    <strong>
                      Total {t('reservations.byDate.confirmedByDate.blockContent.patients').toLocaleLowerCase()}:
                      {_.sumBy(confirmedReservations.data, 'attended')}/{_.sumBy(confirmedReservations.data, 'reserved')}
                    </strong>
                  </Row>

                  <Modal
                    className="deletion-modal"
                    open={modalVisible}
                    onCancel={closeModal}
                    centered={true}
                    closable={false}
                    footer={false}
                  >
                    {
                      reservationToDelete &&
                      <Row justify="center" className="pb-10 pt-10">
                        <p className="text-center mb-10">
                          {t('reservations.byDate.confirmedByDate.modalDeleteText')} <strong>{reservationToDelete.number}</strong>
                        </p>
                        <p className="text-center mb-30">¿Está seguro que desea eliminarla?</p>
                        <Col lg={24} xs={24}>
                          <Row justify="center">
                            <Col lg={7} xs={22} className="mr-lg-20 mb-lg-0 mb-md-15 mb-sm-15 mb-xs-15">
                              <HasAccess
                                roles={['super_usuario', 'branch_office_manager', 'administracion']}
                                permissions='admission'
                              >
                                <Button
                                  className="cmt-button primary outline"
                                  type="primary"
                                  onClick={closeModal}
                                  size="large"
                                  block
                                >
                                  No eliminar
                                </Button>
                              </HasAccess>
                            </Col>
                            <Col lg={10} xs={22}>
                              <HasAccess
                                roles={['super_usuario', 'branch_office_manager', 'administracion']}
                                permissions='admission'
                              >
                                <Button
                                  loading={loadingReservation.byBlock}
                                  onClick={() => reservationDelete(reservationToDelete)}
                                  className="cmt-button primary"
                                  type="primary"
                                  size="large"
                                  block
                                >
                                  {t('reservations.byDate.confirmedByDate.deleteReservationButton')}
                                </Button>
                              </HasAccess>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    }
                  </Modal>
                </Col>
                <Col lg={22} xs={24} className="mb-60">
                  <DateSummary queryParams={queryParams} />
                </Col>
              </Row>
              :
              <Empty
                description={
                  <span style={{ fontSize: 12 }}>
                    {t('reservations.byDate.confirmedByDate.emptyReservations')}
                  </span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
          }
        </Spin>
      </Col>
    </Row>
  );
};

export default ConfirmedReservationByDate;
